// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function ExcelReportTemplatesPage() {
  const pageTitle = "Excel Report Templates";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [title, setTitle] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [openNewtemplateDialog, setOpenNewtemplateDialog] = useState(false);

  async function deleteReportTemplate(reference) {
    setShowLoading(true);

    await axios
      .get(`portal/excel/templates/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const tableColumns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Campaign", accessor: "campaign", align: "left" },
    { Header: "#", accessor: "action", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        name: row.title,
        campaign: row.campaign,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="View Template">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/excel-report-templates/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  visibility
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Delete ${row.title}?`) === true) {
                  deleteReportTemplate(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        // created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/excel/templates.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCampaigns(result.data.data.campaigns);
            const rows = buildRows(result.data.data.reports);
            setReportTemplates(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function uploadExcel() {
    if (selectedCampaign === null) {
      setSeverity("error");
      setErrMsg("Please select a campaign.");
      setOpen(true);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        setSeverity("error");
        setErrMsg("No file found.");
        setOpen(true);
        return;
      }

      setExcelFile(file);
    };

    input.click();
  }

  async function createTemplate() {
    if (title === null) {
      setSeverity("error");
      setErrMsg("A name is required.");
      setOpen(true);
      return;
    }

    if (selectedCampaign === null) {
      setSeverity("error");
      setErrMsg("Please select a campaign.");
      setOpen(true);
      return;
    }

    if (excelFile === null) {
      setSeverity("error");
      setErrMsg("Please select an excel spreadsheet.");
      setOpen(true);
      return;
    }

    setOpenNewtemplateDialog(false);
    setShowLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("campaign", selectedCampaign);
    formData.append("file", excelFile);

    await axios
      .post("portal/excel/templates/create.json", formData)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewtemplateDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>New Template Report</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Select Campaign</InputLabel>
                <Select
                  label="Select Campaign"
                  onChange={(e) => {
                    setSelectedCampaign(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    campaigns.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      campaigns.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
              <MDBox mt={3} align="left">
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => {
                    uploadExcel();
                  }}
                  startIcon={<Icon>upload</Icon>}
                  size="small"
                >
                  Excel Template
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewtemplateDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              createTemplate();
            }}
          >
            Create Template
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={0}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>menu_book</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                        Generate custom excel reports for your data campaigns.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          setTitle(null);
                          setExcelFile(null);
                          setSelectedCampaign(null);
                          setOpenNewtemplateDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Excel Template
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {reportTemplates.length > 0 && (
              <MDBox mt={5}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={0}>
                    <MDBox
                      pt={2}
                      px={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" fontWeight="medium">
                        Report Templates
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={1}>
                      <DataTable
                        table={{ columns: tableColumns, rows: reportTemplates }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ExcelReportTemplatesPage;
